import React from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import Grid from '@mui/material/Grid'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconMenu from '@/v2/components/common/IconMenu'
import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import { DAY_IN_US_FORMAT } from '@/v2/constants'
import { currency } from '@/v2/utils/format'
import PaymentStatus from '../PaymentStatus'
import { useInvoiceOptions } from '@/v2/hooks/usePatientFinance'
import PaidWith from './PaidWith'
import DefaultMethod from './DefaultMethod'
import InvoiceLabel from '../shared/InvoiceLabel'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.palette.white.main};
  ${({ theme, $showBorder }) =>
    $showBorder ? `border-bottom: 1px solid ${theme.palette.silver.dark};` : ''}
  padding: 28px 34px;

  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }
`

const DesktopGrid = styled(Grid)`
  @media (max-width: 768px) {
    display: none !important;
  }
`

const MobileLayout = styled.div`
  display: none;
  width: 100%;

  @media (max-width: 768px) {
    display: block;
  }
`

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 12px;
`

const MobileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`

const MobileItem = styled.div``

const Invoice = ({ invoice, showBorder, paymentMethods, isSingle, plan }) => {
  const theme = useTheme()
  const invoiceOptions = useInvoiceOptions({
    invoice,
    isInstallPayment: true,
  })

  const processingFeeCents = (invoice.payments || []).reduce(
    (sum, payment) => sum + (payment.processingFeeCents || 0),
    0
  )
  const processingFee = processingFeeCents / 100
  const feeRate = Number(invoice.processingFeePercentage) || 0
  const feeLabel = processingFee > 0 && feeRate > 0 ? ` (${feeRate}%)` : ''

  const buildInvoice = () => {
    const invoiceCode = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          INVOICE
        </Typography>
        <Button
          variant="text"
          href={{ pathname: invoice.publicUrl }}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Typography
            fontSize="15px"
            color={theme.palette.link.main}
            sx={{ textDecoration: 'underline' }}
          >
            {`#${invoice.identifierToken}`}
          </Typography>
        </Button>
      </Grid>
    )

    const invoiceDueDate = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          DUE DATE
        </Typography>
        <Typography fontSize="15px">
          {safeFormat(parseISO(invoice.dueDate), DAY_IN_US_FORMAT)}
        </Typography>
      </Grid>
    )

    const invoiceTotal = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          TOTAL
        </Typography>
        <Typography fontSize="15px">
          {currency(Number(invoice.finalAmount).toFixed(2))}
        </Typography>
      </Grid>
    )

    const invoiceTotalPaid = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          TOTAL PAID
        </Typography>
        <Typography fontSize="15px">
          {currency(Number(invoice.paid).toFixed(2))}
        </Typography>
      </Grid>
    )

    const invoiceFee = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          FEE
        </Typography>
        <Typography fontSize="15px">
          {currency(processingFee.toFixed(2))}
          {feeLabel}
        </Typography>
      </Grid>
    )

    const invoiceStatus = (
      <Grid item xs={1}>
        <Typography
          fontSize="10px"
          lineHeight="18px"
          color={theme.palette.gray.dark}
          sx={{ height: '23px' }}
        >
          STATUS
        </Typography>
        <PaymentStatus
          status={invoice.status}
          dueDate={invoice.dueDate}
          paidAt={invoice.paidAt}
          overdue={invoice.overdue}
          payments={invoice.payments}
        />
      </Grid>
    )

    const invoiceDefaultMethod = (
      <DefaultMethod invoice={invoice} paymentMethods={paymentMethods} />
    )

    const invoicePaidWith = <PaidWith invoice={invoice} />

    const invoiceName = (
      <Grid item xs={1}>
        <Typography wordBreak bold lineHeight="23px">
          {plan.description}
        </Typography>
        {plan.contract && (
          <Typography fontSize="12px" color={theme.palette.gray.dark} mb={0.5}>
            Contract #
            {plan.contract.identifierToken || plan.contract.patientLegalName}
          </Typography>
        )}
        <InvoiceLabel status={plan.status} />
      </Grid>
    )

    if (isSingle) {
      return (
        <Wrapper theme={theme} $isSingle={isSingle} $showBorder={showBorder}>
          {/* Desktop Layout */}
          <DesktopGrid container spacing={1} columns={9}>
            {invoiceName}
            {invoiceDueDate}
            {invoiceTotal}
            {invoiceTotalPaid}
            {invoiceFee}
            {invoiceStatus}
            {invoiceCode}
            {invoiceDefaultMethod}
            {invoicePaidWith}
          </DesktopGrid>

          {/* Mobile Layout */}
          <MobileLayout>
            <MobileHeader>
              <div>
                <Typography wordBreak bold lineHeight="23px">
                  {plan.description}
                </Typography>
                {plan.contract && (
                  <Typography
                    fontSize="12px"
                    color={theme.palette.gray.dark}
                    mb={0.5}
                  >
                    Contract #
                    {plan.contract.identifierToken ||
                      plan.contract.patientLegalName}
                  </Typography>
                )}
                <InvoiceLabel status={plan.status} />
              </div>
              <IconMenu
                icon={<MoreVertIcon fontSize="small" />}
                aria-label="More"
                title="More"
                options={invoiceOptions}
              />
            </MobileHeader>
            <MobileGrid>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  DUE DATE
                </Typography>
                <Typography fontSize="14px">
                  {safeFormat(parseISO(invoice.dueDate), DAY_IN_US_FORMAT)}
                </Typography>
              </MobileItem>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  TOTAL
                </Typography>
                <Typography fontSize="14px">
                  {currency(Number(invoice.finalAmount).toFixed(2))}
                </Typography>
              </MobileItem>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  TOTAL PAID
                </Typography>
                <Typography fontSize="14px">
                  {currency(Number(invoice.paid).toFixed(2))}
                </Typography>
              </MobileItem>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  FEE
                </Typography>
                <Typography fontSize="14px">
                  {currency(processingFee.toFixed(2))}
                  {feeLabel}
                </Typography>
              </MobileItem>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  STATUS
                </Typography>
                <PaymentStatus
                  status={invoice.status}
                  dueDate={invoice.dueDate}
                  paidAt={invoice.paidAt}
                  overdue={invoice.overdue}
                  payments={invoice.payments}
                />
              </MobileItem>
              <MobileItem>
                <Typography
                  fontSize="10px"
                  lineHeight="18px"
                  color={theme.palette.gray.dark}
                >
                  INVOICE
                </Typography>
                <Button
                  variant="text"
                  href={{ pathname: invoice.publicUrl }}
                  rel="noopener noreferrer"
                  target="_blank"
                  sx={{ padding: 0, minWidth: 'auto' }}
                >
                  <Typography
                    fontSize="14px"
                    color={theme.palette.link.main}
                    sx={{ textDecoration: 'underline' }}
                  >
                    {`#${invoice.identifierToken}`}
                  </Typography>
                </Button>
              </MobileItem>
              <MobileItem>
                <DefaultMethod
                  invoice={invoice}
                  paymentMethods={paymentMethods}
                  variant="mobile"
                />
              </MobileItem>
              <MobileItem>
                <PaidWith invoice={invoice} variant="mobile" />
              </MobileItem>
            </MobileGrid>
          </MobileLayout>

          <IconMenu
            icon={<MoreVertIcon fontSize="small" />}
            aria-label="More"
            title="More"
            sx={{
              marginLeft: 'auto',
              marginRight: '0',
              display: { xs: 'none', md: 'block' },
            }}
            options={invoiceOptions}
          />
        </Wrapper>
      )
    }

    return (
      <Wrapper theme={theme} $showBorder={showBorder}>
        {/* Desktop Layout */}
        <DesktopGrid container spacing={1} columns={8}>
          {invoiceCode}
          {invoiceDueDate}
          {invoiceTotal}
          {invoiceTotalPaid}
          {invoiceFee}
          {invoiceStatus}
          {invoiceDefaultMethod}
          {invoicePaidWith}
        </DesktopGrid>

        {/* Mobile Layout */}
        <MobileLayout>
          <MobileHeader>
            <div>
              <Button
                variant="text"
                href={{ pathname: invoice.publicUrl }}
                rel="noopener noreferrer"
                target="_blank"
                sx={{ padding: 0, minWidth: 'auto' }}
              >
                <Typography
                  fontSize="14px"
                  color={theme.palette.link.main}
                  sx={{ textDecoration: 'underline' }}
                >
                  Invoice #{invoice.identifierToken}
                </Typography>
              </Button>
            </div>
            <IconMenu
              icon={<MoreVertIcon fontSize="small" />}
              aria-label="More"
              title="More"
              options={invoiceOptions}
            />
          </MobileHeader>
          <MobileGrid>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="18px"
                color={theme.palette.gray.dark}
              >
                DUE DATE
              </Typography>
              <Typography fontSize="14px">
                {safeFormat(parseISO(invoice.dueDate), DAY_IN_US_FORMAT)}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="18px"
                color={theme.palette.gray.dark}
              >
                TOTAL
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(invoice.finalAmount).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="18px"
                color={theme.palette.gray.dark}
              >
                TOTAL PAID
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(invoice.paid).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="18px"
                color={theme.palette.gray.dark}
              >
                FEE
              </Typography>
              <Typography fontSize="14px">
                {currency(processingFee.toFixed(2))}
                {feeLabel}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="18px"
                color={theme.palette.gray.dark}
              >
                STATUS
              </Typography>
              <PaymentStatus
                status={invoice.status}
                dueDate={invoice.dueDate}
                paidAt={invoice.paidAt}
                overdue={invoice.overdue}
                payments={invoice.payments}
              />
            </MobileItem>
            <MobileItem>
              <DefaultMethod
                invoice={invoice}
                paymentMethods={paymentMethods}
                variant="mobile"
              />
            </MobileItem>
            <MobileItem>
              <PaidWith invoice={invoice} variant="mobile" />
            </MobileItem>
          </MobileGrid>
        </MobileLayout>

        <IconMenu
          icon={<MoreVertIcon fontSize="small" />}
          aria-label="More"
          title="More"
          sx={{
            marginLeft: 'auto',
            marginRight: '0',
            display: { xs: 'none', md: 'block' },
          }}
          options={invoiceOptions}
        />
      </Wrapper>
    )
  }

  return buildInvoice()
}

export default Invoice
